import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SchoolCalendar from "@/components/SchoolCalendar";

const EVENT_CATEGORIES = [
  "Outing Pelajar",
  "BBM (Balik bermalam)",
  "Mesyuarat/Taklimat",
  "Lawatan Tetamu Luar",
  "Program Sekolah",
  "Program Asrama",
  "Pemeriksaan Asrama",
  "Gotong-royong",
  "Lain-lain",
];
const EMPTY_EVENT = { title: "", category: "Lain-lain", start_date: "", end_date: "", time: "", venue: "", description: "" };

const formatEventDateRange = (event) => {
  const startValue = event.start_date || event.date;
  const endValue = event.end_date || startValue;
  const format = (value) => new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(`${value}T00:00:00`));
  return startValue === endValue ? format(startValue) : `${format(startValue)} – ${format(endValue)}`;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [section, setSection] = useState("events");

  const load = async () => {
    const response = await api.get("/events");
    setEvents(response.data);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_EVENT);
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditingId(event.id);
    const startDate = event.start_date || event.date;
    setForm({ title: event.title, category: event.category || "Lain-lain", start_date: startDate, end_date: event.end_date || startDate, time: event.time || "", venue: event.venue || "", description: event.description || "" });
    setDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!form.title.trim() || !form.start_date || !form.end_date) return toast.error("Title, start date, and end date are required");
    if (form.end_date < form.start_date) return toast.error("End date must be on or after the start date");
    const eventPayload = { ...form, date: form.start_date };
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, eventPayload);
        toast.success("Event updated");
      } else {
        await api.post("/events", eventPayload);
        toast.success("Event added to calendar");
      }
      setDialogOpen(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save event");
    }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(`Delete “${event.title}”?`)) return;
    try {
      await api.delete(`/events/${event.id}`);
      setEvents((current) => current.filter((item) => item.id !== event.id));
      toast.success("Event deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete event");
    }
  };

  return (
    <div className="space-y-7" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Shared Calendar</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Event &amp; Calendar</h1>
        </div>
        {section === "events" && <Button onClick={openCreate} data-testid="open-event-dialog"><Plus className="h-4 w-4 mr-2" /> New event</Button>}
      </div>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1" data-testid="event-calendar-tabs">
          <TabsTrigger value="events" data-testid="events-tab">Events</TabsTrigger>
          <TabsTrigger value="school-calendar" data-testid="school-calendar-tab">School Calendar</TabsTrigger>
        </TabsList>
      </Tabs>

      {section === "events" && <section className="space-y-4 border-t-4 border-sky-500 pt-4" data-testid="events-list-section">
        {events.length === 0 ? (
          <p className="border border-dashed border-border p-10 text-center text-muted-foreground">No events yet.</p>
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {events.map((event) => {
              const date = new Date(`${event.date}T00:00:00`);
              return (
                <article key={event.id} className="flex flex-wrap items-start justify-between gap-4 py-5" data-testid={`event-item-${event.id}`}>
                  <div className="flex min-w-0 gap-4">
                    <div className="w-14 shrink-0 text-center text-primary">
                      <p className="text-xs font-bold uppercase">{date.toLocaleString("en-US", { month: "short" })}</p>
                      <p className="font-display text-2xl font-extrabold leading-tight">{date.getDate()}</p>
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-display text-lg font-bold">{event.title}</h2>
                      {event.category && <p className="mt-1 text-sm font-medium text-primary">{event.category}</p>}
                      <p className="mt-1 text-sm text-muted-foreground">{formatEventDateRange(event)}</p>
                      <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {event.time && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{event.time}</span>}
                        {event.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{event.venue}</span>}
                      </p>
                      {event.description && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{event.description}</p>}
                      <p className="mt-2 text-xs text-muted-foreground">Added by {event.author_name}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(event)} aria-label={`Edit ${event.title}`} title="Edit event"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteEvent(event)} aria-label={`Delete ${event.title}`} title="Delete event"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>}
      {section === "school-calendar" && <SchoolCalendar />}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit event" : "Create an event"}</DialogTitle>
            <DialogDescription>Events are shared with everyone and are removed automatically after two days.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" /></div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(category) => setForm({ ...form, category })}>
                <SelectTrigger data-testid="event-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_CATEGORIES.map((category) => <SelectItem key={category} value={category}>{category}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Start date</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="event-start-date-input" /></div>
              <div className="space-y-2"><Label>End date</Label><Input type="date" required min={form.start_date || undefined} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="event-end-date-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time-input" />
            </div>
            <div className="space-y-2"><Label>Venue</Label><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="event-venue-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveEvent} data-testid="event-submit-btn">{editingId ? "Save changes" : "Add event"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}